import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ConfirmModal from "../../common/ConfirmModal";
import InfoAlert from "../../common/InfoAlert";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import { useScheduleAccess } from "./scheduleAccess";
import CapaCreateModal from "../capa/CapaCreateModal";
import {
  buildCapaContextFromStudy,
  studyRowCapaDisplayLabel,
  studyRowCapaId,
} from "../capa/capaConstants";
import DropdownMenu from "../../common/DropDownMenu/dropdownmenu";
import {
  GROUP_TYPE,
  MIN_MULTI_TREATMENT_GROUPS,
  SETUP_TOGGLES,
  buildMultiGroupStep1Payload,
  buildMultiGroupStep2Payload,
  buildSingleGroupPayload,
  defaultWizardState,
  flagsFromStudyRow,
  getStepsForGroupType,
  resolveCohortSaveStudyId,
  resolveSetupSaveVisitCount,
} from "./scheduleWorkflow";
import {
  addAdjacentVisitToStudy,
  buildWizardPatchFromCohort,
  fetchCohortSetup,
  fetchScheduleAuditLog,
  hasCohortGroupRow,
  mergeCohortFromSave,
  mergeDraftsAfterVisitReload,
  persistOneVisitDetails,
  publishScheduleLive,
  mergeCohortAfterPublish,
  parseScheduleLiveFlag,
  removeVisitFromStudy,
  saveMultiGroupStep1,
  saveMultiGroupStep2,
  saveSingleGroupSetup,
  saveUpdateDeleteGroup,
  swapVisitOrder,
  syncVisitStructure,
  visitPreviewRows,
  writeScheduleAuditLog,
} from "./scheduleService";
import { patchApiVisitRowsWithScreeningDrafts, visitHasPersistableDetails } from "./scheduleVisitPersistence";
import ScheduleVisitBuilder from "./ScheduleVisitBuilder";
import {
  buildMultiGroupVisitCohorts,
  cohortVisitGroups,
  cohortsForGroupDropdown,
  countVisitsForApi,
  createVisitDraft,
  isAdhocVisit,
  normalizeVisitDrafts,
  patchPlannerSnapshotInVisits,
  resolveStudyScreeningGroupId,
  treatmentGroupNames,
  visitsFromApiRows,
  visitsForVisitRail,
  visitsWithoutAdhoc,
  VISIT_TYPE,
} from "./scheduleVisitDraft";
import "./ScheduleEditor.scss";
import ScheduleStepper from "./ScheduleStepper";
import ScheduleActivityLogPanel from "./ScheduleActivityLogPanel";
import ScheduleActivityAiSummaryButton from "./ScheduleActivityAiSummaryButton";
import ScheduleActivityAiSummaryModal from "./ScheduleActivityAiSummaryModal";
import ScheduleReadinessTopBadge from "./ScheduleReadinessTopBadge";
import { buildScheduleActivityAiSummary } from "./scheduleActivityAiSummary";
import { useScheduleWorkspace } from "./scheduleWorkspaceContext";
import {
  ARCHIVED_STUDY_READONLY_MESSAGE,
  isArchivedStudyReadOnly,
} from "../studymanagement/studyArchivedReadOnly";
import {
  SCHEDULE_AUDIT_COLUMNS,
  buildSetupAuditPayload,
  formatDocumentAudit,
  formatGroupChange,
  formatVisitAdded,
  formatVisitDeleted,
  formatVisitTimePoint,
} from "./scheduleAuditEvents";
import SchedulePreviewPanel from "./SchedulePreviewPanel";
import {
  authenticateScheduleSave,
  SCHEDULE_DRAFT_SAVE_MESSAGE,
  SCHEDULE_DRAFT_UPDATE_MESSAGE,
  SCHEDULE_LIVE_BANNER,
  SCHEDULE_LIVE_NO_DELETE_MESSAGE,
  SCHEDULE_LIVE_NO_REORDER_MESSAGE,
  SCHEDULE_LIVE_SAVE_MESSAGE,
  SCHEDULE_LIVE_SAVED_DETAILS_MESSAGE,
  SCHEDULE_VISIT_DELETE_MESSAGE,
} from "./scheduleSaveGate";

const pick = (obj, ...keys) => {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

function badgeTextColor(background) {
  const raw = String(background || "").trim().replace("#", "");
  if (raw.length < 6) return "#ffffff";
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return "#ffffff";
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.58 ? "#0f172a" : "#ffffff";
}

function ToggleRow({ id, label, hint, offImpact, checked, disabled, onChange }) {
  return (
    <div
      className={`schedule-toggle-row${disabled ? " is-locked" : ""}${
        checked ? "" : " is-off"
      }`}
    >
      <div className="schedule-toggle-row__text">
        <label htmlFor={id} className="schedule-toggle-row__label">
          {label}
          {!checked ? (
            <span className="schedule-toggle-row__off-badge">Off</span>
          ) : null}
        </label>
        {checked && hint ? (
          <p className="schedule-toggle-row__hint">{hint}</p>
        ) : null}
        {!checked && (offImpact || hint) ? (
          <p className="schedule-toggle-row__impact">
            <span className="schedule-toggle-row__impact-label">Impact on schedule:</span>{" "}
            {offImpact || hint}
          </p>
        ) : null}
      </div>
      <div className="form-check form-switch schedule-toggle-row__switch m-0">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id={id}
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </div>
    </div>
  );
}

export default function ScheduleEditor() {
  const { studyId: routeStudyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    embedded,
    onClose,
    scheduleId: embeddedScheduleId,
    studyRow: embeddedStudyRow,
  } = useScheduleWorkspace();
  const studyId = embedded
    ? String(embeddedScheduleId || "").trim()
    : routeStudyId;
  const { userData, loading: userLoading } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { canAddEditSchedule, canViewStudyList, canCreateCapa } =
    useScheduleAccess(userData);
  const [showCapaCreateModal, setShowCapaCreateModal] = useState(false);
  const [showEmbeddedActions, setShowEmbeddedActions] = useState(false);
  const embeddedActionsRef = useRef(null);

  const studyFromNav = embedded
    ? embeddedStudyRow
    : location.state?.study || null;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [visitStructureBusy, setVisitStructureBusy] = useState(false);
  const [savingVisitKey, setSavingVisitKey] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [maxReachable, setMaxReachable] = useState(0);
  const [wizard, setWizard] = useState(defaultWizardState);
  const [cohort, setCohort] = useState(null);
  const [scheduleCreated, setScheduleCreated] = useState(false);
  const [isScheduleLive, setIsScheduleLive] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [publishSummaryOpen, setPublishSummaryOpen] = useState(false);
  const [publishSummaryLoading, setPublishSummaryLoading] = useState(false);
  const [publishSummaryError, setPublishSummaryError] = useState("");
  const [publishSummary, setPublishSummary] = useState(null);
  const [readinessRefreshKey, setReadinessRefreshKey] = useState(0);
  const [saveConfirm, setSaveConfirm] = useState({
    open: false,
    title: "Confirm save",
    message: SCHEDULE_DRAFT_SAVE_MESSAGE,
    confirmText: "Continue",
    confirmButtonClass: "btn-primary",
    onProceed: null,
  });
  const loadStartedRef = useRef(false);
  const saveConfirmResolverRef = useRef(null);
  const initialSetupFlagsRef = useRef(null);
  const initialSetupGroupTypeRef = useRef(null);
  const [groupManageBusy, setGroupManageBusy] = useState(false);

  const isStudyArchived = useMemo(
    () =>
      isArchivedStudyReadOnly(studyFromNav) ||
      isArchivedStudyReadOnly(cohort?.study),
    [studyFromNav, cohort?.study],
  );
  const scheduleMutationsLocked = isScheduleLive || isStudyArchived;

  const trialStudyId = useMemo(
    () =>
      cohort?.trialStudyId ||
      pick(studyFromNav, "StudyId", "studyId") ||
      studyId ||
      "",
    [cohort?.trialStudyId, studyFromNav, studyId],
  );

  const setupFlagsChanged = useCallback(() => {
    const baseline = initialSetupFlagsRef.current;
    if (!baseline) return true;
    return SETUP_TOGGLES.some(
      (toggle) => !!wizard.setupFlags[toggle.key] !== !!baseline[toggle.key],
    );
  }, [wizard.setupFlags]);
  const showAlertRef = useRef(showAlert);
  showAlertRef.current = showAlert;

  const steps = useMemo(
    () => getStepsForGroupType(wizard.groupType),
    [wizard.groupType],
  );
  const activeStep = steps[stepIndex]?.id || "setup";

  const studyTitle = useMemo(() => {
    return (
      cohort?.studyName ||
      pick(studyFromNav, "StudyName", "studyName") ||
      "Study"
    );
  }, [cohort, studyFromNav]);

  const studyNumber = useMemo(
    () =>
      pick(studyFromNav, "StudyNumber", "studyNumber") ||
      pick(cohort?.study, "StudyNumber", "studyNumber") ||
      "",
    [cohort?.study, studyFromNav],
  );

  const studyColor = useMemo(
    () =>
      pick(
        studyFromNav,
        "StudySchColor",
        "studySchColor",
        "StudyColor",
        "studyColor",
      ) ||
      cohort?.studyColor ||
      wizard.studyColor ||
      "",
    [cohort?.studyColor, studyFromNav, wizard.studyColor],
  );

  const breadcrumbStudyLabel = useMemo(
    () => (
      <span className="schedule-breadcrumb-study">
        {studyNumber ? (
          <span
            className="schedule-breadcrumb-study__number-badge"
            style={{
              backgroundColor: studyColor || "#64748b",
              color: badgeTextColor(studyColor),
            }}
            title="Study number"
          >
            {studyNumber}
          </span>
        ) : null}
        <span className="schedule-breadcrumb-study__title">{studyTitle}</span>
      </span>
    ),
    [studyColor, studyNumber, studyTitle],
  );

  const handleCreateCapa = useCallback(() => {
    if (isStudyArchived) {
      showAlert({
        title: "Archived study",
        message: ARCHIVED_STUDY_READONLY_MESSAGE,
        type: "info",
      });
      return;
    }
    const studyIdForCapa = studyRowCapaId(studyFromNav) || trialStudyId;
    if (!studyIdForCapa) return;
    setShowCapaCreateModal(true);
  }, [isStudyArchived, showAlert, studyFromNav, trialStudyId]);

  const capaCreateContext = useMemo(() => {
    const studyIdForCapa = studyRowCapaId(studyFromNav) || trialStudyId;
    if (!studyIdForCapa) return null;
    return buildCapaContextFromStudy({
      studyId: studyIdForCapa,
      studyLabel:
        studyRowCapaDisplayLabel(studyFromNav) ||
        [studyNumber, studyTitle].filter(Boolean).join(" — "),
    });
  }, [studyFromNav, studyNumber, studyTitle, trialStudyId]);

  const handleRefresh = () => {
    loadStartedRef.current = null;
    if (userData?.OrganizationId) {
      loadSetup(userData.OrganizationId);
      loadAuditLog();
    }
  };

  const schedulePageActions = useMemo(
    () => {
      const list = [
        {
          icon: `fas fa-sync-alt${loading ? " fa-spin" : ""}`,
          label: "Refresh",
          onClick: handleRefresh,
        },
      ];
      if (canCreateCapa && !isStudyArchived) {
        list.push({
          icon: "fas fa-clipboard-list",
          label: "Create CAPA",
          onClick: handleCreateCapa,
        });
      }
      return list;
    },
    // handleRefresh is recreated each render; keep actions in sync with visible state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canCreateCapa, handleCreateCapa, isStudyArchived, loading],
  );

  const loadSetup = async (organizationId) => {
    if (!studyId || !organizationId) return;
    setLoading(true);
    try {
      const parsed = await fetchCohortSetup({
        studyId,
        organizationId,
      });
      setCohort(parsed);
      setScheduleCreated(parsed.isScheduleDone);
      setIsScheduleLive(
        parsed.isScheduleLive || parseScheduleLiveFlag(parsed.study),
      );

      const hasSavedSetup =
        parsed.isScheduleDone || hasCohortGroupRow(parsed.studyGroup);

      initialSetupFlagsRef.current = hasSavedSetup
        ? buildWizardPatchFromCohort(parsed, defaultWizardState()).setupFlags
        : defaultWizardState().setupFlags;
      initialSetupGroupTypeRef.current = hasSavedSetup
        ? buildWizardPatchFromCohort(parsed, defaultWizardState()).groupType
        : defaultWizardState().groupType;

      setWizard((prev) => {
        const patch = buildWizardPatchFromCohort(parsed, prev);
        return {
          ...prev,
          ...patch,
          setupFlags: hasSavedSetup ? patch.setupFlags : prev.setupFlags,
        };
      });

      const stepDefs = getStepsForGroupType(
        parsed.groupType || GROUP_TYPE.SINGLE,
      );
      const startStep = hasSavedSetup
        ? Math.min(1, stepDefs.length - 1)
        : 0;
      setStepIndex(startStep);
      setMaxReachable(hasSavedSetup ? stepDefs.length - 1 : 0);
    } catch (err) {
      showAlertRef.current({
        title: "Schedule setup",
        message: err.message || "Failed to load schedule.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const organizationId = userData?.OrganizationId;
    if (userLoading || !organizationId || !studyId) {
      return;
    }
    loadSetup(organizationId);
  }, [studyId, userData?.OrganizationId, userLoading]);

  const updateWizard = (patch) => setWizard((prev) => ({ ...prev, ...patch }));
  const updateFlag = (key, value) =>
    setWizard((prev) => {
      const nextFlags = { ...prev.setupFlags, [key]: value };
      if (key === "studyScreening" && !value) {
        nextFlags.screeningRandomisationTogether = false;
      }
      return { ...prev, setupFlags: nextFlags };
    });

  const applySetupFlagsToVisits = useCallback((setupFlags) => {
    setWizard((prev) => {
      if (prev.groupType === GROUP_TYPE.MULTIPLE) {
        const screeningGroupId = resolveStudyScreeningGroupId(
          cohort?.visits,
          prev.groupNames,
        );
        const nextGroupVisitDrafts = { ...prev.groupVisitDrafts };
        if (!setupFlags.studyScreening && screeningGroupId) {
          delete nextGroupVisitDrafts[screeningGroupId];
        }
        treatmentGroupNames(prev.groupNames).forEach((group) => {
          const groupId =
            pick(group, "GroupNameId", "groupNameId") ||
            pick(group, "GroupNameIdRef", "groupNameIdRef");
          if (!groupId) return;
          nextGroupVisitDrafts[groupId] = visitsForVisitRail(
            nextGroupVisitDrafts[groupId] || prev.visitDrafts,
          );
        });
        return {
          ...prev,
          setupFlags,
          visitDrafts: visitsForVisitRail(prev.visitDrafts),
          groupVisitDrafts: nextGroupVisitDrafts,
        };
      }
      return {
        ...prev,
        setupFlags,
        visitDrafts: visitsForVisitRail(prev.visitDrafts),
        groupVisitDrafts: Object.fromEntries(
          Object.entries(prev.groupVisitDrafts).map(([id, rows]) => [
            id,
            visitsForVisitRail(rows),
          ]),
        ),
      };
    });
  }, [cohort?.visits]);

  const cohortSaveStudyId = useMemo(
    () =>
      resolveCohortSaveStudyId({
        trialStudyId,
        studyScheduleId: cohort?.studyScheduleId,
        studyFromNav,
      }),
    [cohort?.studyScheduleId, studyFromNav, trialStudyId],
  );

  const loadAuditLog = useCallback(async () => {
    const organizationId = userData?.OrganizationId;
    if (!organizationId || !trialStudyId) return;
    const studyScheduleId = cohort?.studyScheduleId || cohortSaveStudyId;
    setAuditLoading(true);
    try {
      const rows = await fetchScheduleAuditLog({
        trialStudyId,
        studyScheduleId,
        organizationId,
      });
      setAuditLog(rows);
    } catch {
      setAuditLog([]);
    } finally {
      setAuditLoading(false);
    }
  }, [
    cohort?.studyScheduleId,
    cohortSaveStudyId,
    trialStudyId,
    userData?.OrganizationId,
  ]);

  useEffect(() => {
    if (!loading && trialStudyId && userData?.OrganizationId) {
      loadAuditLog();
    }
  }, [loading, loadAuditLog, trialStudyId, userData?.OrganizationId]);

  const baseIds = useMemo(
    () => ({
      studyId: cohortSaveStudyId,
      trialStudyId,
      organizationId: userData?.OrganizationId,
      createdBy: userData?.StaffId,
      studyColor: wizard.studyColor,
      setupFlags: wizard.setupFlags,
    }),
    [
      cohortSaveStudyId,
      trialStudyId,
      userData,
      wizard.studyColor,
      wizard.setupFlags,
    ],
  );

  const bumpReadiness = useCallback(() => {
    setReadinessRefreshKey((current) => current + 1);
  }, []);

  const handleVisitPlannerSnapshotPatch = useCallback((visitRef, patch) => {
    if (!visitRef || !patch) return;
    setWizard((prev) => {
      const patchList = (visits) => patchPlannerSnapshotInVisits(visits, visitRef, patch);
      const next = { ...prev };
      if (Array.isArray(prev.visitDrafts)) {
        next.visitDrafts = patchList(prev.visitDrafts);
      }
      if (prev.groupVisitDrafts && typeof prev.groupVisitDrafts === "object") {
        next.groupVisitDrafts = Object.fromEntries(
          Object.entries(prev.groupVisitDrafts).map(([groupId, visits]) => [
            groupId,
            patchList(visits),
          ]),
        );
      }
      return next;
    });
  }, []);

  const recordAudit = useCallback(
    async ({
      actionLabel,
      comments = "",
      columnName = SCHEDULE_AUDIT_COLUMNS.VISITS,
      oldValue = "",
      newValue = "",
      studyScheduleId: studyScheduleIdOverride = null,
    }) => {
      const studyScheduleId =
        studyScheduleIdOverride ||
        cohort?.studyScheduleId ||
        cohortSaveStudyId ||
        trialStudyId;
      if (
        !trialStudyId ||
        !baseIds.organizationId ||
        !baseIds.createdBy ||
        !actionLabel
      ) {
        throw new Error("Schedule activity could not be recorded (missing context).");
      }
      try {
        await writeScheduleAuditLog({
          trialStudyId,
          studyScheduleId,
          organizationId: baseIds.organizationId,
          createdBy: baseIds.createdBy,
          actionLabel,
          comments,
          columnName,
          oldValue,
          newValue,
        });
        await loadAuditLog();
      } catch (err) {
        console.warn("Schedule activity log write failed:", err?.message || err);
        showAlert?.({
          title: "Activity log",
          message:
            err?.message ||
            "Could not record this action in the activity log. Deploy audit SQL and restart the API.",
          type: "warning",
        });
        throw err;
      }
    },
    [
      baseIds.createdBy,
      baseIds.organizationId,
      cohort?.studyScheduleId,
      cohortSaveStudyId,
      loadAuditLog,
      showAlert,
      trialStudyId,
    ],
  );

  const goToStepById = useCallback(
    (stepId) => {
      const idx = steps.findIndex((step) => step.id === stepId);
      if (idx >= 0) {
        setStepIndex(idx);
        setMaxReachable((current) => Math.max(current, idx));
      }
    },
    [steps],
  );

  const goNext = () => {
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    setMaxReachable((m) => Math.max(m, stepIndex + 1));
  };

  const handleStepClick = async (index) => {
    if (index === stepIndex) return;

    if (steps[index]?.id === "activity") {
      setStepIndex(index);
      return;
    }

    if (index < stepIndex || (index <= maxReachable && index !== stepIndex + 1)) {
      setStepIndex(index);
      return;
    }

    if (index !== stepIndex + 1) return;

    const stepId = steps[stepIndex]?.id;
    if (stepId === "setup") {
      await handleSetupContinue();
      return;
    }
    if (stepId === "groups-visits") {
      await handleGroupsVisitsContinue();
      return;
    }
    if (stepId === "visits") {
      handleVisitsContinue();
      return;
    }
    goNext();
  };

  const scheduleSaveMessage = (isUpdate) => {
    if (isScheduleLive) return SCHEDULE_LIVE_SAVE_MESSAGE;
    if (isUpdate) return SCHEDULE_DRAFT_UPDATE_MESSAGE;
    return SCHEDULE_DRAFT_SAVE_MESSAGE;
  };

  const promptScheduleSave = (onProceed, message = SCHEDULE_DRAFT_SAVE_MESSAGE) =>
    new Promise((resolve) => {
      const isUpdate =
        message === SCHEDULE_DRAFT_UPDATE_MESSAGE ||
        message === SCHEDULE_LIVE_SAVE_MESSAGE;
      saveConfirmResolverRef.current = resolve;
      setSaveConfirm({
        open: true,
        title: isUpdate || isScheduleLive ? "Confirm update" : "Confirm save",
        message,
        confirmText: "Continue",
        confirmButtonClass: "btn-primary",
        onProceed: async () => {
          setSaveConfirm((prev) => ({ ...prev, open: false, onProceed: null }));
          const auth = await authenticateScheduleSave({ showAlert });
          if (!auth.ok) {
            saveConfirmResolverRef.current?.(false);
            saveConfirmResolverRef.current = null;
            return;
          }
          try {
            await onProceed(auth.comments);
            saveConfirmResolverRef.current?.(true);
          } catch {
            saveConfirmResolverRef.current?.(false);
          } finally {
            saveConfirmResolverRef.current = null;
          }
        },
      });
    });

  const closeSaveConfirm = () => {
    saveConfirmResolverRef.current?.(false);
    saveConfirmResolverRef.current = null;
    setSaveConfirm((prev) => ({ ...prev, open: false, onProceed: null }));
  };

  const promptAuditedVisitAction = ({
    title,
    message,
    confirmText,
    confirmButtonClass = "btn-primary",
    onProceed,
  }) =>
    new Promise((resolve) => {
      saveConfirmResolverRef.current = resolve;
      setSaveConfirm({
        open: true,
        title,
        message,
        confirmText,
        confirmButtonClass,
        onProceed: async () => {
          setSaveConfirm((prev) => ({ ...prev, open: false, onProceed: null }));
          const auth = await authenticateScheduleSave({ showAlert });
          if (!auth.ok) {
            saveConfirmResolverRef.current?.(false);
            saveConfirmResolverRef.current = null;
            return;
          }
          try {
            await onProceed(auth.comments);
            saveConfirmResolverRef.current?.(true);
          } catch {
            saveConfirmResolverRef.current?.(false);
          } finally {
            saveConfirmResolverRef.current = null;
          }
        },
      });
    });

  const handleSetupContinue = async () => {
    if (isStudyArchived) {
      goNext();
      return;
    }
    if (!baseIds.organizationId || !baseIds.createdBy) return;

    const needsSave = !scheduleCreated || setupFlagsChanged();
    if (needsSave) {
      const confirmed = await promptScheduleSave(
        async (comments) => {
          await runSetupSave(comments);
          await loadAuditLog();
        },
        scheduleSaveMessage(scheduleCreated),
      );
      if (!confirmed) return;
      return;
    }

    goNext();
  };

  const handleSetupSaveOnly = async () => {
    if (isStudyArchived) {
      showAlert({
        title: "Archived study",
        message: ARCHIVED_STUDY_READONLY_MESSAGE,
        type: "info",
      });
      return;
    }
    if (!baseIds.organizationId || !baseIds.createdBy) return;
    const needsSave = !scheduleCreated || setupFlagsChanged();
    if (!needsSave) {
      showAlert({
        title: "Setup",
        message: "No setup changes to save.",
        type: "info",
      });
      return;
    }
    await promptScheduleSave(
      async (comments) => {
        await runSetupSave(comments, { advance: false });
        await loadAuditLog();
        showAlert({
          title: "Setup",
          message: scheduleCreated ? "Setup updated." : "Setup saved.",
          type: "success",
        });
        bumpReadiness();
      },
      scheduleSaveMessage(scheduleCreated),
    );
  };

  const runSetupSave = async (comments, { advance = true } = {}) => {
    setSubmitting(true);
    showLoader("Saving schedule setup…");
    const savedFlags = { ...wizard.setupFlags };
    const setupVisitCount = resolveSetupSaveVisitCount(savedFlags);
    try {
      if (wizard.groupType === GROUP_TYPE.SINGLE) {
        const rows = await saveSingleGroupSetup(
          buildSingleGroupPayload({
            ...baseIds,
            noOfVisits: setupVisitCount,
            setupFlags: savedFlags,
            action: "Create",
            comments,
          }),
        );
        const merged = mergeCohortFromSave(cohort || {}, rows, savedFlags);
        const studyScheduleId =
          pick(rows[0], "StudyScheduleId", "studyScheduleId") ||
          pick(rows[0], "StudyIdRef", "studyIdRef") ||
          merged.studyScheduleId ||
          cohortSaveStudyId;

        let reloaded = merged;
        try {
          reloaded = await fetchCohortSetup({
            studyId: studyScheduleId,
            organizationId: baseIds.organizationId,
          });
        } catch {
          // Keep optimistic merge when reload fails.
        }

        const screening = savedFlags.studyScreening;
        const apiDrafts = visitsFromApiRows(reloaded.visits);
        const visitDrafts = visitsForVisitRail(apiDrafts);

        setCohort(reloaded);
        const setupBaseline = initialSetupFlagsRef.current || {};
        const baselineGroupType =
          initialSetupGroupTypeRef.current ?? wizard.groupType;
        const isSetupUpdate = scheduleCreated;
        setScheduleCreated(true);
        initialSetupFlagsRef.current = { ...savedFlags };
        initialSetupGroupTypeRef.current = wizard.groupType;
        setWizard((prev) => ({
          ...prev,
          ...buildWizardPatchFromCohort(reloaded, prev),
          setupFlags: savedFlags,
          visitDrafts,
          noOfVisits: Math.max(1, visitsWithoutAdhoc(visitDrafts).length),
        }));
        const setupAudit = buildSetupAuditPayload({
          baselineFlags: setupBaseline,
          savedFlags,
          groupType: wizard.groupType,
          baselineGroupType,
          isUpdate: isSetupUpdate,
        });
        await recordAudit({
          ...setupAudit,
          comments: comments || "",
          columnName: SCHEDULE_AUDIT_COLUMNS.SETUP,
          studyScheduleId,
        });
        if (advance) goNext();
        return;
      }

      const rows = await saveMultiGroupStep1(
        buildMultiGroupStep1Payload({ ...baseIds, comments }),
      );
      let merged = mergeCohortFromSave(cohort || {}, rows, savedFlags);
      try {
        const reloaded = await fetchCohortSetup({
          studyId: cohortSaveStudyId,
          organizationId: baseIds.organizationId,
        });
        merged = mergeCohortFromSave(
          {
            ...reloaded,
            groupNames:
              reloaded.groupNames?.length > 0
                ? reloaded.groupNames
                : merged.groupNames,
          },
          rows,
          savedFlags,
        );
      } catch {
        // Keep optimistic merge when reload fails (offline / API down).
      }
      setCohort(merged);
      const setupBaseline = initialSetupFlagsRef.current || {};
      const baselineGroupType =
        initialSetupGroupTypeRef.current ?? wizard.groupType;
      const isSetupUpdate = scheduleCreated;
      setScheduleCreated(true);
      initialSetupFlagsRef.current = { ...savedFlags };
      initialSetupGroupTypeRef.current = wizard.groupType;
      setWizard((prev) => ({
        ...prev,
        ...buildWizardPatchFromCohort(merged, prev),
        setupFlags: savedFlags,
      }));
      applySetupFlagsToVisits(savedFlags);
      const setupAudit = buildSetupAuditPayload({
        baselineFlags: setupBaseline,
        savedFlags,
        groupType: wizard.groupType,
        baselineGroupType,
        isUpdate: isSetupUpdate,
      });
      const multiStudyScheduleId =
        pick(rows[0], "StudyScheduleId", "studyScheduleId") ||
        pick(rows[0], "StudyIdRef", "studyIdRef") ||
        merged.studyScheduleId ||
        cohortSaveStudyId;
      await recordAudit({
        ...setupAudit,
        comments: comments || "",
        columnName: SCHEDULE_AUDIT_COLUMNS.SETUP,
        studyScheduleId: multiStudyScheduleId,
      });
      if (advance) goNext();
    } catch (err) {
      showAlert({
        title: "Setup",
        message: err.message,
        type: "error",
      });
      throw err;
    } finally {
      hideLoader();
      setSubmitting(false);
    }
  };

  const visitBuilderMode = useMemo(() => {
    if (wizard.groupType === GROUP_TYPE.MULTIPLE) {
      return "cohorts";
    }
    return "single";
  }, [wizard.groupType]);

  const visitCohorts = useMemo(() => {
    if (wizard.groupType === GROUP_TYPE.MULTIPLE) {
      return buildMultiGroupVisitCohorts({
        groupNames: wizard.groupNames,
        groupVisitDrafts: wizard.groupVisitDrafts,
        visitDrafts: wizard.visitDrafts,
        apiVisits: cohort?.visits || [],
        studyScreening: wizard.setupFlags.studyScreening,
        allGroupsSameSchedule: wizard.allGroupsSameSchedule,
      });
    }
    return cohortVisitGroups(
      wizard.groupNames,
      wizard.groupVisitDrafts,
      wizard.setupFlags.studyScreening,
    );
  }, [
    cohort?.visits,
    wizard.allGroupsSameSchedule,
    wizard.groupNames,
    wizard.groupType,
    wizard.groupVisitDrafts,
    wizard.setupFlags.studyScreening,
    wizard.visitDrafts,
  ]);

  const reloadCohortWizard = useCallback(async () => {
    const reloaded = await fetchCohortSetup({
      studyId,
      organizationId: baseIds.organizationId,
    });
    setCohort(reloaded);
    setWizard((prev) => ({
      ...prev,
      ...buildWizardPatchFromCohort(reloaded, prev),
      noOfGroups: Math.max(
        treatmentGroupNames(reloaded.groupNames || []).length,
        prev.noOfGroups,
      ),
    }));
    return reloaded;
  }, [baseIds.organizationId, studyId]);

  const syncAllGroupsSameSchedule = useCallback(
    async (nextValue) => {
      const treatmentCount = treatmentGroupNames(wizard.groupNames).length;
      if (!cohort?.studyGroupId || treatmentCount < MIN_MULTI_TREATMENT_GROUPS) {
        updateWizard({ allGroupsSameSchedule: nextValue });
        return;
      }
      setGroupManageBusy(true);
      showLoader("Updating group schedule…");
      try {
        await saveMultiGroupStep2(
          buildMultiGroupStep2Payload({
            studyId: trialStudyId,
            studyScheduleId: cohort?.studyScheduleId,
            studyGroupId: cohort.studyGroupId,
            organizationId: baseIds.organizationId,
            createdBy: baseIds.createdBy,
            noOfGroups: treatmentCount,
            allGroupsSameSchedule: nextValue,
          }),
        );
        updateWizard({ allGroupsSameSchedule: nextValue });
        await reloadCohortWizard();
        await loadAuditLog();
      } catch (err) {
        showAlert({
          title: "Groups",
          message: err.message || "Could not update group schedule.",
          type: "error",
        });
      } finally {
        hideLoader();
        setGroupManageBusy(false);
      }
    },
    [
      baseIds.createdBy,
      baseIds.organizationId,
      cohort?.studyGroupId,
      cohort?.studyScheduleId,
      hideLoader,
      loadAuditLog,
      reloadCohortWizard,
      showAlert,
      showLoader,
      trialStudyId,
      wizard.groupNames,
    ],
  );

  const handleAddTreatmentGroup = useCallback(async () => {
    const treatmentGroups = treatmentGroupNames(wizard.groupNames);
    if (treatmentGroups.length >= 20) {
      showAlert({
        title: "Groups",
        message: "Maximum 20 groups allowed.",
        type: "warning",
      });
      return;
    }
    if (!cohort?.studyGroupId) {
      showAlert({
        title: "Groups",
        message: "Study group is not ready. Complete setup first.",
        type: "error",
      });
      return;
    }

    const name = `Group ${treatmentGroups.length + 1}`;

    const confirmed = await promptScheduleSave(
      async (comments) => {
        setGroupManageBusy(true);
        showLoader("Adding group…");
        try {
          await saveUpdateDeleteGroup({
            OrganizationIdRef: baseIds.organizationId,
            ActionType: "Save",
            StudyGroupId: cohort.studyGroupId,
            GroupName: name,
            CreatedBy: baseIds.createdBy,
          });
          await reloadCohortWizard();
          await recordAudit({
            actionLabel: `Group added: ${name}`,
            newValue: formatGroupChange({ newName: name, action: "added" }),
            comments: comments || "",
            columnName: SCHEDULE_AUDIT_COLUMNS.GROUPS,
            studyScheduleId: cohort?.studyScheduleId || cohortSaveStudyId,
          });
        } catch (err) {
          showAlert({ title: "Groups", message: err.message, type: "error" });
          throw err;
        } finally {
          hideLoader();
          setGroupManageBusy(false);
        }
        await loadAuditLog();
      },
      scheduleSaveMessage(false),
    );
    if (!confirmed) return;
  }, [
    baseIds.createdBy,
    baseIds.organizationId,
    cohort?.studyGroupId,
    hideLoader,
    loadAuditLog,
    recordAudit,
    reloadCohortWizard,
    showAlert,
    showLoader,
    wizard.groupNames,
  ]);

  const handleRenameTreatmentGroup = useCallback(
    async (groupId, name) => {
      const trimmed = String(name || "").trim();
      if (!trimmed) {
        showAlert({
          title: "Groups",
          message: "Enter a group name.",
          type: "warning",
        });
        return;
      }
      if (!cohort?.studyGroupId || !groupId) {
        showAlert({
          title: "Groups",
          message: "Study group is not ready. Complete setup first.",
          type: "error",
        });
        return;
      }

      const existing = treatmentGroupNames(wizard.groupNames).find(
        (group) =>
          String(
            pick(group, "GroupNameId", "groupNameId") ||
              pick(group, "GroupNameIdRef", "groupNameIdRef") ||
              "",
          ) === String(groupId),
      );
      const currentName = String(
        pick(existing, "GroupName", "groupName") || "",
      ).trim();
      if (trimmed === currentName) return;

      const confirmed = await promptScheduleSave(
        async (comments) => {
          setGroupManageBusy(true);
          showLoader("Updating group…");
          try {
            await saveUpdateDeleteGroup({
              OrganizationIdRef: baseIds.organizationId,
              ActionType: "Update",
              StudyGroupId: cohort.studyGroupId,
              GroupNameId: groupId,
              GroupName: trimmed,
              CreatedBy: baseIds.createdBy,
            });
            await reloadCohortWizard();
            await recordAudit({
              actionLabel: `Group updated: ${trimmed}`,
              oldValue: `Group: ${currentName}`,
              newValue: `Group: ${trimmed}`,
              comments: comments || "",
              columnName: SCHEDULE_AUDIT_COLUMNS.GROUPS,
              studyScheduleId: cohort?.studyScheduleId || cohortSaveStudyId,
            });
          } catch (err) {
            showAlert({ title: "Groups", message: err.message, type: "error" });
            throw err;
          } finally {
            hideLoader();
            setGroupManageBusy(false);
          }
          await loadAuditLog();
        },
        scheduleSaveMessage(true),
      );
      if (!confirmed) {
        throw new Error("cancelled");
      }
    },
    [
      baseIds.createdBy,
      baseIds.organizationId,
      cohort?.studyGroupId,
      hideLoader,
      loadAuditLog,
      recordAudit,
      reloadCohortWizard,
      showAlert,
      showLoader,
      wizard.groupNames,
    ],
  );

  const handleDeleteTreatmentGroup = useCallback(
    async (groupId, groupName) => {
      const treatmentCount = treatmentGroupNames(wizard.groupNames).length;
      if (treatmentCount <= MIN_MULTI_TREATMENT_GROUPS) {
        showAlert({
          title: "Groups",
          message: `At least ${MIN_MULTI_TREATMENT_GROUPS} treatment groups are required.`,
          type: "warning",
        });
        return;
      }
      if (!cohort?.studyGroupId || !groupId) return;

      await promptAuditedVisitAction({
        title: "Delete group",
        message: `Delete "${groupName}"? Visits for this group will be removed.`,
        confirmText: "Delete",
        confirmButtonClass: "btn-danger",
        onProceed: async (comments) => {
          setGroupManageBusy(true);
          showLoader("Deleting group…");
          try {
            await saveUpdateDeleteGroup({
              OrganizationIdRef: baseIds.organizationId,
              ActionType: "Delete",
              StudyGroupId: cohort.studyGroupId,
              GroupNameId: groupId,
              CreatedBy: baseIds.createdBy,
            });
            await reloadCohortWizard();
            await recordAudit({
              actionLabel: `Group deleted: ${groupName}`,
              newValue: formatGroupChange({ oldName: groupName, action: "deleted" }),
              comments: comments || "",
              columnName: SCHEDULE_AUDIT_COLUMNS.GROUPS,
              studyScheduleId: cohort?.studyScheduleId || cohortSaveStudyId,
            });
          } catch (err) {
            showAlert({ title: "Groups", message: err.message, type: "error" });
            throw err;
          } finally {
            hideLoader();
            setGroupManageBusy(false);
          }
          await loadAuditLog();
        },
      });
    },
    [
      baseIds.createdBy,
      baseIds.organizationId,
      cohort?.studyGroupId,
      hideLoader,
      loadAuditLog,
      recordAudit,
      reloadCohortWizard,
      showAlert,
      showLoader,
      wizard.groupNames,
    ],
  );

  const groupManageProps = useMemo(() => {
    if (wizard.groupType !== GROUP_TYPE.MULTIPLE) return null;
    return {
      disabled: scheduleMutationsLocked,
      busy: groupManageBusy || submitting,
      onSameScheduleChange: syncAllGroupsSameSchedule,
      onAdd: handleAddTreatmentGroup,
      onSaveGroupName: handleRenameTreatmentGroup,
      onDeleteGroup: handleDeleteTreatmentGroup,
    };
  }, [
    groupManageBusy,
    handleAddTreatmentGroup,
    handleDeleteTreatmentGroup,
    handleRenameTreatmentGroup,
    scheduleMutationsLocked,
    submitting,
    syncAllGroupsSameSchedule,
    wizard.groupType,
  ]);

  useEffect(() => {
    if (activeStep !== "groups-visits" && activeStep !== "visits") return;
    if (wizard.groupType === GROUP_TYPE.MULTIPLE) return;

    setWizard((prev) => ({
      ...prev,
      visitDrafts: visitsForVisitRail(prev.visitDrafts),
      groupVisitDrafts: Object.fromEntries(
        Object.entries(prev.groupVisitDrafts).map(([key, rows]) => [
          key,
          visitsForVisitRail(rows),
        ]),
      ),
    }));
  }, [activeStep, wizard.groupType]);

  const applyReloadedVisits = useCallback(
    (reloaded, userDrafts, { groupId = null } = {}) => {
      const isMulti = wizard.groupType === GROUP_TYPE.MULTIPLE;
      const screeningGroupId = isMulti
        ? resolveStudyScreeningGroupId(reloaded.visits, reloaded.groupNames)
        : null;
      const isScreeningGroup =
        groupId && screeningGroupId && String(groupId) === String(screeningGroupId);
      const screening = isMulti
        ? !!isScreeningGroup
        : wizard.setupFlags.studyScreening;

      if (groupId) {
        const merged = mergeDraftsAfterVisitReload({
          userDrafts,
          apiRows: reloaded.visits,
          screening: isScreeningGroup ? true : false,
          groupNameId: groupId,
        });
        const normalized = visitsForVisitRail(merged);
        setWizard((prev) => {
          const patch = buildWizardPatchFromCohort(reloaded, prev);
          const nextGroupVisitDrafts = {
            ...patch.groupVisitDrafts,
            ...prev.groupVisitDrafts,
            [groupId]: normalized,
          };
          if (isScreeningGroup) {
            return { ...prev, ...patch, groupVisitDrafts: nextGroupVisitDrafts };
          }
          const treatmentIds = treatmentGroupNames(patch.groupNames || prev.groupNames)
            .map(
              (group) =>
                pick(group, "GroupNameId", "groupNameId") ||
                pick(group, "GroupNameIdRef", "groupNameIdRef"),
            )
            .filter(Boolean);
          if (prev.allGroupsSameSchedule) {
            treatmentIds.forEach((id) => {
              nextGroupVisitDrafts[id] = normalized;
            });
          }
          return {
            ...prev,
            ...patch,
            visitDrafts: normalized,
            groupVisitDrafts: nextGroupVisitDrafts,
            noOfVisits: visitsWithoutAdhoc(normalized).length,
          };
        });
        return normalized;
      }

      const merged = mergeDraftsAfterVisitReload({
        userDrafts,
        apiRows: reloaded.visits,
        screening,
      });
      const normalized = visitsForVisitRail(merged);
      setWizard((prev) => ({
        ...prev,
        ...buildWizardPatchFromCohort(reloaded, prev),
        visitDrafts: normalized,
        noOfVisits: visitsWithoutAdhoc(normalized).length,
      }));
      return normalized;
    },
    [wizard.groupType, wizard.setupFlags.studyScreening],
  );

  const reloadVisitsFromApi = useCallback(
    async (userDrafts, { groupId = null } = {}) => {
      const reloaded = await fetchCohortSetup({
        studyId,
        organizationId: baseIds.organizationId,
      });
      const patched = {
        ...reloaded,
        visits: patchApiVisitRowsWithScreeningDrafts(reloaded.visits, userDrafts),
      };
      setCohort(patched);
      return applyReloadedVisits(patched, userDrafts, { groupId });
    },
    [applyReloadedVisits, baseIds.organizationId, studyId],
  );

  const handleAddVisit = useCallback(
    async ({ referenceVisit, visitName, groupId = null, isStudyScreeningGroup = false }) => {
      if (isStudyArchived) {
        showAlert({
          title: "Archived study",
          message: ARCHIVED_STUDY_READONLY_MESSAGE,
          type: "info",
        });
        return null;
      }
      if (isStudyScreeningGroup) {
        showAlert({
          title: "Study Screening",
          message: "Study screening has a single screening visit configured at setup.",
          type: "info",
        });
        return null;
      }
      if (!baseIds.organizationId || !baseIds.createdBy) return null;
      const cohortMeta = groupId
        ? visitCohorts.find((cohort) => cohort.id === groupId)
        : null;
      const screening = cohortMeta?.isStudyScreeningGroup
        ? true
        : wizard.groupType === GROUP_TYPE.MULTIPLE
          ? false
          : wizard.setupFlags.studyScreening;
      const trimmedName = String(visitName || "").trim();
      if (!trimmedName) {
        showAlert({
          title: "Visits",
          message: "Enter a visit name.",
          type: "warning",
        });
        return null;
      }

      const studyScheduleId = cohort?.studyScheduleId || cohortSaveStudyId;
      if (!studyScheduleId) {
        showAlert({
          title: "Visits",
          message: "Study schedule is not ready. Complete setup steps first.",
          type: "warning",
        });
        return null;
      }

      const currentDrafts = visitsForVisitRail(
        groupId ? wizard.groupVisitDrafts[groupId] || [] : wizard.visitDrafts,
      );
      const nameTaken = currentDrafts.some(
        (visit) =>
          String(visit.visitName || "").trim().toLowerCase() ===
          trimmedName.toLowerCase(),
      );
      if (nameTaken) {
        showAlert({
          title: "Visits",
          message: "A visit with this name already exists in this schedule.",
          type: "warning",
        });
        return null;
      }

      const savedBefore = new Set(
        currentDrafts
          .map((visit) => visit.visitsPerStudyId)
          .filter(Boolean)
          .map(String),
      );

      const anchor =
        referenceVisit?.visitsPerStudyId && !isAdhocVisit(referenceVisit)
          ? referenceVisit
          : [...currentDrafts]
              .reverse()
              .find((visit) => visit.visitsPerStudyId && !isAdhocVisit(visit)) || null;

      setVisitStructureBusy(true);
      showLoader("Adding visit…");
      try {
        if (anchor?.visitsPerStudyId) {
          await addAdjacentVisitToStudy({
            studyScheduleId,
            referenceVisit: anchor,
            visitName: trimmedName,
            organizationId: baseIds.organizationId,
            createdBy: baseIds.createdBy,
          });
        } else {
          const nextVisits = [
            ...currentDrafts,
            createVisitDraft({
              visitName: trimmedName,
              visitType: VISIT_TYPE.NON_RESIDENTIAL,
            }),
          ];
          const dbVisitCount = Array.isArray(cohort?.visits) ? cohort.visits.length : 0;
          await syncVisitStructure({
            trialStudyId,
            studyScheduleId,
            studyGroupId: cohort?.studyGroupId,
            organizationId: baseIds.organizationId,
            createdBy: baseIds.createdBy,
            noOfVisits: Math.max(
              countVisitsForApi(nextVisits, { screening }),
              dbVisitCount + 1,
            ),
            setupFlags: wizard.setupFlags,
            studyColor: wizard.studyColor,
            groupNameId: groupId,
            groupType: wizard.groupType,
            allGroupsSameSchedule: wizard.allGroupsSameSchedule,
            isStudyScreeningGroup: !!visitCohorts.find(
              (cohort) => cohort.id === groupId,
            )?.isStudyScreeningGroup,
          });
        }

        const merged = await reloadVisitsFromApi(currentDrafts, { groupId });
        const added =
          merged.find(
            (visit) =>
              visit.visitsPerStudyId &&
              !savedBefore.has(String(visit.visitsPerStudyId)),
          ) ||
          merged.find((visit) =>
            String(visit.visitName || "")
              .trim()
              .toLowerCase() === trimmedName.toLowerCase(),
          );

        await recordAudit({
          actionLabel: `Visit added: ${trimmedName}`,
          newValue: formatVisitAdded(added || { visitName: trimmedName }, {
            groupName: cohortMeta?.name,
          }),
          comments: anchor?.visitName
            ? `Added after "${anchor.visitName}"`
            : "",
          columnName: SCHEDULE_AUDIT_COLUMNS.VISITS,
          studyScheduleId,
        });
        return {
          visitsPerStudyId: added?.visitsPerStudyId || null,
          visitName: added?.visitName || trimmedName,
        };
      } catch (err) {
        showAlert({ title: "Visits", message: err.message, type: "error" });
        return null;
      } finally {
        hideLoader();
        setVisitStructureBusy(false);
      }
    },
    [
      baseIds.createdBy,
      baseIds.organizationId,
      cohort?.studyGroupId,
      cohort?.studyScheduleId,
      cohort?.visits,
      cohortSaveStudyId,
      hideLoader,
      loadAuditLog,
      recordAudit,
      reloadVisitsFromApi,
      showAlert,
      showLoader,
      trialStudyId,
      wizard.allGroupsSameSchedule,
      wizard.groupType,
      wizard.groupVisitDrafts,
      wizard.setupFlags,
      wizard.studyColor,
      wizard.visitDrafts,
      visitCohorts,
      isStudyArchived,
    ],
  );

  const handleSyncVisitStructure = useCallback(
    async ({ nextVisits, groupId = null, skipAudit = false }) => {
      if (!baseIds.organizationId || !baseIds.createdBy) return;
      const cohortMeta = groupId
        ? visitCohorts.find((cohort) => cohort.id === groupId)
        : null;
      const screening = cohortMeta
        ? !!cohortMeta.isStudyScreeningGroup
        : wizard.setupFlags.studyScreening;
      const normalized = normalizeVisitDrafts(nextVisits, { screening });
      if (normalized.length > 99) {
        showAlert({
          title: "Visits",
          message: "Maximum 99 visits allowed.",
          type: "warning",
        });
        return;
      }

      const studyScheduleId = cohort?.studyScheduleId || cohortSaveStudyId;
      if (!studyScheduleId) {
        showAlert({
          title: "Visits",
          message: "Study schedule is not ready. Complete setup steps first.",
          type: "warning",
        });
        return;
      }

      setVisitStructureBusy(true);
      showLoader("Saving visit…");
      try {
        await syncVisitStructure({
          trialStudyId,
          studyScheduleId,
          studyGroupId: cohort?.studyGroupId,
          organizationId: baseIds.organizationId,
          createdBy: baseIds.createdBy,
          noOfVisits: countVisitsForApi(normalized, { screening }),
          setupFlags: wizard.setupFlags,
          studyColor: wizard.studyColor,
          groupNameId: groupId,
          groupType: wizard.groupType,
          allGroupsSameSchedule: wizard.allGroupsSameSchedule,
          isStudyScreeningGroup: !!visitCohorts.find(
            (cohort) => cohort.id === groupId,
          )?.isStudyScreeningGroup,
        });
        await reloadVisitsFromApi(normalized, { groupId });
        if (!skipAudit) {
          await recordAudit({
            actionLabel: "Visit structure saved",
            comments: `${countVisitsForApi(normalized, { screening })} visit(s) in schedule`,
            columnName: SCHEDULE_AUDIT_COLUMNS.VISITS,
            studyScheduleId,
          });
        }
      } catch (err) {
        showAlert({ title: "Visits", message: err.message, type: "error" });
      } finally {
        hideLoader();
        setVisitStructureBusy(false);
      }
    },
    [
      baseIds.createdBy,
      baseIds.organizationId,
      cohort?.studyGroupId,
      cohort?.studyScheduleId,
      cohortSaveStudyId,
      loadAuditLog,
      recordAudit,
      reloadVisitsFromApi,
      showAlert,
      showLoader,
      hideLoader,
      trialStudyId,
      wizard.allGroupsSameSchedule,
      wizard.groupType,
      wizard.setupFlags,
      wizard.studyColor,
      visitCohorts,
    ],
  );

  const handleRequestRemoveVisit = useCallback(
    async ({ visit, groupId = null }) => {
      if (!visit || isAdhocVisit(visit) || visit.isScreening) return;
      if (isStudyArchived) {
        showAlert({
          title: "Archived study",
          message: ARCHIVED_STUDY_READONLY_MESSAGE,
          type: "info",
        });
        return;
      }
      if (isScheduleLive) {
        showAlert({
          title: "Live schedule",
          message: SCHEDULE_LIVE_NO_DELETE_MESSAGE,
          type: "warning",
        });
        return;
      }
      const studyScheduleId = cohort?.studyScheduleId || cohortSaveStudyId;
      if (!studyScheduleId) {
        showAlert({
          title: "Visits",
          message: "Study schedule is not ready.",
          type: "warning",
        });
        return;
      }

      const visitName = String(visit.visitName || "this visit").trim();
      await promptAuditedVisitAction({
        title: "Delete visit",
        message: `Are you sure you want to delete "${visitName}"? ${SCHEDULE_VISIT_DELETE_MESSAGE}`,
        confirmText: "Delete",
        confirmButtonClass: "btn-danger",
        onProceed: async (authComments) => {
          setVisitStructureBusy(true);
          showLoader("Removing visit…");
          try {
            await removeVisitFromStudy({
              studyScheduleId,
              visit,
              organizationId: baseIds.organizationId,
              createdBy: baseIds.createdBy,
            });
            const currentDrafts = visitsForVisitRail(
              groupId ? wizard.groupVisitDrafts[groupId] || [] : wizard.visitDrafts,
            );
            const nextDrafts = currentDrafts.filter((row) => row.id !== visit.id);
            await reloadVisitsFromApi(nextDrafts, { groupId });
            await recordAudit({
              actionLabel: `Visit deleted: ${visitName}`,
              newValue: formatVisitDeleted(visit, {
                groupName: visitCohorts.find((c) => c.id === groupId)?.name,
              }),
              comments: authComments || "",
              columnName: SCHEDULE_AUDIT_COLUMNS.VISITS,
              studyScheduleId,
            });
            showAlert({
              title: "Visits",
              message: "Visit removed.",
              type: "success",
            });
          } catch (err) {
            showAlert({ title: "Visits", message: err.message, type: "error" });
            throw err;
          } finally {
            hideLoader();
            setVisitStructureBusy(false);
          }
        },
      });
    },
    [
      baseIds.createdBy,
      baseIds.organizationId,
      cohort?.studyScheduleId,
      cohortSaveStudyId,
      hideLoader,
      loadAuditLog,
      recordAudit,
      reloadVisitsFromApi,
      showAlert,
      showLoader,
      wizard.groupVisitDrafts,
      wizard.setupFlags.studyScreening,
      wizard.visitDrafts,
      isScheduleLive,
      isStudyArchived,
    ],
  );

  const handleReorderVisit = useCallback(
    async ({ visit, fromIndex, toIndex, groupId = null }) => {
      if (!visit || isAdhocVisit(visit) || visit.isScreening) return;
      if (fromIndex === toIndex) return;
      if (isStudyArchived) {
        showAlert({
          title: "Archived study",
          message: ARCHIVED_STUDY_READONLY_MESSAGE,
          type: "info",
        });
        return;
      }
      if (isScheduleLive) {
        showAlert({
          title: "Live schedule",
          message: SCHEDULE_LIVE_NO_REORDER_MESSAGE,
          type: "warning",
        });
        return;
      }
      const studyScheduleId = cohort?.studyScheduleId || cohortSaveStudyId;
      if (!studyScheduleId) {
        showAlert({
          title: "Visits",
          message: "Study schedule is not ready.",
          type: "warning",
        });
        return;
      }

      const steps = Math.abs(toIndex - fromIndex);
      const direction = toIndex < fromIndex ? "up" : "down";

      setVisitStructureBusy(true);
      showLoader("Updating visit order…");
      try {
        for (let step = 0; step < steps; step += 1) {
          await swapVisitOrder({
            studyScheduleId,
            visit,
            direction,
            organizationId: baseIds.organizationId,
            createdBy: baseIds.createdBy,
          });
        }
        await reloadVisitsFromApi([], { groupId });
        await recordAudit({
          actionLabel: `Visit reordered: ${String(visit.visitName || "Visit").trim()}`,
          comments: `Moved ${direction === "up" ? "up" : "down"} in the visit list`,
          columnName: SCHEDULE_AUDIT_COLUMNS.VISITS,
          studyScheduleId,
        });
      } catch (err) {
        showAlert({ title: "Visits", message: err.message, type: "error" });
      } finally {
        hideLoader();
        setVisitStructureBusy(false);
      }
    },
    [
      baseIds.createdBy,
      baseIds.organizationId,
      cohort?.studyScheduleId,
      cohortSaveStudyId,
      hideLoader,
      loadAuditLog,
      recordAudit,
      reloadVisitsFromApi,
      showAlert,
      showLoader,
      isScheduleLive,
      isStudyArchived,
    ],
  );

  const handleSaveVisitDetails = useCallback(
    async ({ visit, section, groupId = null, priorVisit = null }) => {
      if (!visit || isAdhocVisit(visit)) return;
      if (isStudyArchived) {
        showAlert({
          title: "Archived study",
          message: ARCHIVED_STUDY_READONLY_MESSAGE,
          type: "info",
        });
        return;
      }
      const visitKey = visit.id || visit.visitsPerStudyId;
      const studyScheduleId = cohort?.studyScheduleId || cohortSaveStudyId;
      if (!studyScheduleId) {
        showAlert({
          title: "Visit setup",
          message: "Study schedule is not ready.",
          type: "warning",
        });
        return;
      }

      const cohortMeta = groupId
        ? visitCohorts.find((cohort) => cohort.id === groupId)
        : null;
      const screening = cohortMeta
        ? !!cohortMeta.isStudyScreeningGroup
        : wizard.setupFlags.studyScreening;
      const currentDrafts = visitsForVisitRail(
        groupId ? wizard.groupVisitDrafts[groupId] || [] : wizard.visitDrafts,
      );
      const prior =
        priorVisit ||
        currentDrafts.find(
          (row) =>
            row.id === visit.id ||
            (visit.visitsPerStudyId &&
              String(row.visitsPerStudyId) === String(visit.visitsPerStudyId)),
        );
      if (isScheduleLive) {
        const fillingEmptyTimePoint =
          (section === "time_point" || section === "screening") &&
          !visitHasPersistableDetails(prior, { screening });
        if (!fillingEmptyTimePoint) {
          showAlert({
            title: "Live schedule",
            message: SCHEDULE_LIVE_SAVED_DETAILS_MESSAGE,
            type: "warning",
          });
          return;
        }
      }

      setSavingVisitKey(visitKey);
      showLoader("Saving visit details…");
      try {
        const priorSnapshot = prior ? createVisitDraft(prior) : null;
        await persistOneVisitDetails({
          studyScheduleId,
          organizationId: baseIds.organizationId,
          createdBy: baseIds.createdBy,
          visit,
          visits: currentDrafts,
          screening,
          section,
        });
        const nextDrafts = currentDrafts.map((row) =>
          row.id === visit.id ||
          (visit.visitsPerStudyId &&
            row.visitsPerStudyId &&
            String(row.visitsPerStudyId) === String(visit.visitsPerStudyId))
            ? visit
            : row,
        );
        await reloadVisitsFromApi(nextDrafts, { groupId });
        const visitLabel = String(visit.visitName || "Visit").trim();
        const priorLabel = String(priorSnapshot?.visitName || "").trim();
        const priorTimePoint = formatVisitTimePoint(priorSnapshot, {
          referenceVisits: currentDrafts,
        });
        const nextTimePoint = formatVisitTimePoint(visit, {
          referenceVisits: currentDrafts,
        });
        let actionLabel = "Visit details saved";
        let oldValue = "";
        let newValue = "";

        if (section === "visit_name") {
          actionLabel = priorLabel ? "Visit renamed" : "Visit renamed";
          oldValue = priorLabel ? `Visit: ${priorLabel}` : "";
          newValue = `Visit: ${visitLabel}`;
        } else if (section === "screening" || section === "time_point") {
          actionLabel = priorTimePoint
            ? "Visit time point updated"
            : "Visit time point saved";
          oldValue = priorTimePoint;
          newValue = nextTimePoint;
        }

        await recordAudit({
          actionLabel,
          oldValue,
          newValue,
          comments: `Visit: ${visitLabel}`,
          columnName: SCHEDULE_AUDIT_COLUMNS.VISITS,
          studyScheduleId,
        });
        showAlert({
          title: "Visit setup",
          message:
            section === "visit_name"
              ? "Visit name updated."
              : "Visit details saved.",
          type: "success",
        });
        bumpReadiness();
      } catch (err) {
        showAlert({ title: "Visit setup", message: err.message, type: "error" });
        throw err;
      } finally {
        hideLoader();
        setSavingVisitKey(null);
      }
    },
    [
      baseIds.createdBy,
      baseIds.organizationId,
      bumpReadiness,
      cohort?.studyScheduleId,
      cohortSaveStudyId,
      recordAudit,
      reloadVisitsFromApi,
      showAlert,
      showLoader,
      hideLoader,
      isScheduleLive,
      isStudyArchived,
      wizard.groupVisitDrafts,
      wizard.setupFlags.studyScreening,
      wizard.visitDrafts,
      visitCohorts,
    ],
  );

  const handleVisitsContinue = () => {
    const screening = wizard.setupFlags.studyScreening;
    const dropdownCohorts = cohortsForGroupDropdown(visitCohorts, {
      allGroupsSameSchedule: wizard.allGroupsSameSchedule,
    });
    const cohortLists =
      visitBuilderMode === "cohorts"
        ? dropdownCohorts.map((c) => ({
            label: c.name,
            isStudyScreeningGroup: !!c.isStudyScreeningGroup,
            visits: visitsForVisitRail(c.visits),
          }))
        : [
            {
              label: "Visits",
              isStudyScreeningGroup: false,
              visits: visitsForVisitRail(wizard.visitDrafts),
            },
          ];

    const treatmentLists = cohortLists.filter((c) => !c.isStudyScreeningGroup);

    if (wizard.groupType === GROUP_TYPE.MULTIPLE && !treatmentLists.length) {
      showAlert({
        title: "Groups & Visits",
        message: `Add at least ${MIN_MULTI_TREATMENT_GROUPS} groups using + in the group selector.`,
        type: "warning",
      });
      return;
    }

    if (
      wizard.groupType === GROUP_TYPE.MULTIPLE &&
      treatmentLists.length < MIN_MULTI_TREATMENT_GROUPS
    ) {
      showAlert({
        title: "Groups & Visits",
        message: `Add at least ${MIN_MULTI_TREATMENT_GROUPS} treatment groups before continuing.`,
        type: "warning",
      });
      return;
    }

    if (
      !treatmentLists.length ||
      treatmentLists.some((list) => !visitsWithoutAdhoc(list.visits).length)
    ) {
      showAlert({
        title: "Groups & Visits",
        message: "Add at least one visit to each treatment group.",
        type: "warning",
      });
      return;
    }

    if (screening && wizard.groupType === GROUP_TYPE.MULTIPLE) {
      const screeningList = cohortLists.find((c) => c.isStudyScreeningGroup);
      const screeningVisits = visitsWithoutAdhoc(screeningList?.visits || []);
      if (
        !screeningVisits.length ||
        screeningVisits.some((visit) => !visit.visitsPerStudyId)
      ) {
        showAlert({
          title: "Groups & Visits",
          message: "Study screening visit is not saved yet. Complete setup first.",
          type: "warning",
        });
        return;
      }
    }

    const missingDbId = cohortLists.some((list) =>
      visitsWithoutAdhoc(list.visits).some((visit) => !visit.visitsPerStudyId),
    );
    if (missingDbId || visitStructureBusy) {
      showAlert({
        title: "Groups & Visits",
        message: "Wait for visits to finish saving to the database.",
        type: "warning",
      });
      return;
    }

    goNext();
  };

  const handleGroupsVisitsContinue = async () => {
    if (wizard.groupType === GROUP_TYPE.MULTIPLE) {
      const treatmentCount = treatmentGroupNames(wizard.groupNames).length;
      if (treatmentCount < MIN_MULTI_TREATMENT_GROUPS) {
        showAlert({
          title: "Groups & Visits",
          message: `Add at least ${MIN_MULTI_TREATMENT_GROUPS} treatment groups before continuing.`,
          type: "warning",
        });
        return;
      }
    }
    handleVisitsContinue();
  };

  const handlePublish = async () => {
    if (isStudyArchived) {
      showAlert({
        title: "Archived study",
        message: ARCHIVED_STUDY_READONLY_MESSAGE,
        type: "info",
      });
      return;
    }
    const scheduleId =
      cohort?.studyScheduleId ||
      pick(studyFromNav, "StudyScheduleId", "studyScheduleId");
    if (!scheduleId) {
      showAlert({
        title: "Go live",
        message: "Study schedule id is missing. Complete visits first.",
        type: "warning",
      });
      return;
    }

    setPublishSummaryOpen(true);
    setPublishSummaryLoading(true);
    setPublishSummaryError("");
    setPublishSummary(null);
    try {
      const result = await buildScheduleActivityAiSummary({
        auditLog,
        wizard,
        cohort,
        scheduleCreated,
        visitBuilderMode,
        visitCohorts,
        studyScheduleId: scheduleId,
        organizationId: baseIds.organizationId,
        createdBy: baseIds.createdBy,
        isScheduleLive,
      });
      setPublishSummary(result);
    } catch (err) {
      setPublishSummaryError(err?.message || "Could not verify schedule readiness.");
    } finally {
      setPublishSummaryLoading(false);
    }
  };

  const handlePublishContinue = async () => {
    if (isStudyArchived) {
      showAlert({
        title: "Archived study",
        message: ARCHIVED_STUDY_READONLY_MESSAGE,
        type: "info",
      });
      return;
    }
    if (!publishSummary?.canGoLive || submitting) return;

    const scheduleId =
      cohort?.studyScheduleId ||
      pick(studyFromNav, "StudyScheduleId", "studyScheduleId");
    if (!scheduleId) return;

    setPublishSummaryOpen(false);

    const auth = await authenticateScheduleSave({ showAlert });
    if (!auth.ok) return;

    setSubmitting(true);
    const wasDraftPublish = !isScheduleLive;
    showLoader(isScheduleLive ? "Updating schedule…" : "Publishing schedule…");
    try {
      await publishScheduleLive({
        studyScheduleId: scheduleId,
        studyId: trialStudyId,
        organizationId: baseIds.organizationId,
        createdBy: baseIds.createdBy,
        comments: auth.comments,
      });
      setCohort((prev) => mergeCohortAfterPublish(prev));
      showAlert({
        title: wasDraftPublish ? "Schedule live" : "Schedule updated",
        message: wasDraftPublish
          ? "Study schedule has been published successfully."
          : "Live schedule has been updated successfully.",
        type: "success",
      });
      if (userData?.OrganizationId) {
        loadStartedRef.current = null;
        await loadSetup(userData.OrganizationId);
      }
      setIsScheduleLive(true);
      setCohort((prev) => mergeCohortAfterPublish(prev));
      await loadAuditLog();
      bumpReadiness();
      goNext();
    } catch (err) {
      showAlert({ title: "Go live", message: err.message, type: "error" });
    } finally {
      hideLoader();
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (activeStep === "activity") {
      loadAuditLog();
    }
  }, [activeStep, loadAuditLog]);

  const previewVisits = useMemo(() => {
    if (visitBuilderMode === "cohorts" && visitCohorts.length) {
      return visitCohorts.flatMap((c) =>
        c.visits.map((v, i) => ({
          VisitName: `${c.name}: ${v.visitName}`,
          VisitOrder: i + 1,
          visitType: v.visitType,
        })),
      );
    }
    const drafts = visitsForVisitRail(wizard.visitDrafts);
    if (drafts.length) {
      return drafts.map((v, i) => ({
        VisitName: v.visitName,
        VisitOrder: i + 1,
        visitType: v.visitType,
      }));
    }
    return visitPreviewRows(cohort?.visits, wizard.noOfVisits);
  }, [
    cohort?.visits,
    visitBuilderMode,
    visitCohorts,
    wizard.noOfVisits,
    wizard.setupFlags.studyScreening,
    wizard.visitDrafts,
  ]);

  if (userLoading || !userData) {
    return (
      <div className="schedule-editor schedule-editor--loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!canViewStudyList || !canAddEditSchedule) {
    if (embedded) {
      return (
        <div className="schedule-editor schedule-editor--embedded schedule-editor--denied">
          <p>You do not have permission to open this schedule.</p>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      );
    }
    return <Navigate to="/notification-center" replace />;
  }

  if (!studyId) {
    if (embedded) {
      return (
        <div className="schedule-editor schedule-editor--embedded schedule-editor--denied">
          <p>Could not open this schedule. The schedule id is missing.</p>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      );
    }
    return <Navigate to="/studymanagement" replace />;
  }

  const pageStatusAlert = isStudyArchived ? (
    <div className="schedule-readonly-alert" role="status">
      <i className="fas fa-archive schedule-readonly-alert__icon" aria-hidden />
      <span>{ARCHIVED_STUDY_READONLY_MESSAGE}</span>
    </div>
  ) : isScheduleLive ? (
    <div className="schedule-readonly-alert" role="status">
      <i className="fas fa-lock schedule-readonly-alert__icon" aria-hidden />
      <span>{SCHEDULE_LIVE_BANNER}</span>
    </div>
  ) : null;

  const headerScheduleStatus =
    scheduleCreated && !isScheduleLive && !isStudyArchived ? (
      <span
        className="schedule-status-chip schedule-status-chip--draft"
        title="Draft schedule — you can update setup questions until you publish. Group type cannot be changed."
      >
        Draft schedule
      </span>
    ) : null;

  const headerReadiness = (
    <div className="schedule-editor__header-readiness">
      {headerScheduleStatus}
      <ScheduleReadinessTopBadge
        wizard={wizard}
        cohort={cohort}
        scheduleCreated={scheduleCreated}
        visitBuilderMode={visitBuilderMode}
        visitCohorts={visitCohorts}
        studyScheduleId={cohort?.studyScheduleId || cohortSaveStudyId}
        organizationId={baseIds.organizationId}
        createdBy={baseIds.createdBy}
        refreshKey={readinessRefreshKey}
      />
    </div>
  );

  const embeddedActionsControl =
    schedulePageActions.length > 1 ? (
      <div className="position-relative">
        <button
          ref={embeddedActionsRef}
          type="button"
          className="actions-trigger"
          onClick={() => setShowEmbeddedActions((prev) => !prev)}
          aria-expanded={showEmbeddedActions}
          aria-haspopup="true"
        >
          Actions
          <i
            className={`fas fa-chevron-down${
              showEmbeddedActions ? " rotate-180" : ""
            }`}
          />
        </button>
        <DropdownMenu
          items={schedulePageActions.map((action) => ({
            ...action,
            onClick: () => {
              setShowEmbeddedActions(false);
              action.onClick?.();
            },
          }))}
          show={showEmbeddedActions}
          onClose={() => setShowEmbeddedActions(false)}
          trigger={embeddedActionsRef}
          align="start"
        />
      </div>
    ) : (
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={handleRefresh}
        disabled={loading}
      >
        <i className={`fas fa-sync-alt${loading ? " fa-spin" : ""}`} aria-hidden="true" />
        <span>Refresh</span>
      </button>
    );

  return (
    <div className={`schedule-editor${embedded ? " schedule-editor--embedded" : ""}`}>
      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
      <CapaCreateModal
        open={showCapaCreateModal}
        context={capaCreateContext}
        titleId="schedule-capa-create-title"
        onClose={() => setShowCapaCreateModal(false)}
        onSaved={() => {
          setShowCapaCreateModal(false);
          showAlert({
            title: "CAPA created",
            message: "Record has been saved successfully.",
            type: "success",
          });
        }}
      />

      <ScheduleActivityAiSummaryModal
        open={publishSummaryOpen}
        onClose={() => {
          if (!submitting) setPublishSummaryOpen(false);
        }}
        loading={publishSummaryLoading}
        error={publishSummaryError}
        summary={publishSummary}
        isScheduleLive={isScheduleLive}
        variant="publish"
        onContinue={handlePublishContinue}
        continueLabel={
          isScheduleLive ? "Continue to update live schedule" : "Continue to publish"
        }
        continueBusy={submitting}
      />

      <div className="schedule-editor__topbar">
        {embedded ? (
          <div className="schedule-editor__embedded-head">
            <div className="schedule-editor__embedded-start">
              <div className="schedule-editor__embedded-title">
                <h5 id="schedule-workspace-title">Schedule readiness</h5>
                <div className="schedule-editor__embedded-study">{breadcrumbStudyLabel}</div>
              </div>
              <div className="schedule-editor__embedded-actions">
                {embeddedActionsControl}
              </div>
            </div>
            <div className="schedule-editor__header-alert">
              {pageStatusAlert}
            </div>
            <div className="schedule-editor__embedded-end">
              {headerReadiness}
              <button
                type="button"
                className="btn-close"
                aria-label="Close schedule"
                onClick={onClose}
              />
            </div>
          </div>
        ) : (
          <>
            <PageBreadcrumb
              items={[
                { label: "Home", path: "/notification-center" },
                { label: "Study Management", path: "/studymanagement" },
                { label: breadcrumbStudyLabel },
              ]}
              actions={schedulePageActions}
            />
            <div className="schedule-editor__header-details">
              {pageStatusAlert}
              {headerReadiness}
            </div>
          </>
        )}
      </div>

      <ScheduleStepper
        steps={steps}
        activeIndex={stepIndex}
        maxReachable={maxReachable}
        onStepClick={handleStepClick}
      />

      <div className="schedule-editor__workspace schedule-editor__workspace--full">
        <div className="schedule-editor__main">
      {loading ? (
        <div className="schedule-panel card border-0 shadow-sm schedule-panel--loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading schedule…</span>
          </div>
        </div>
      ) : (
        <div
          className={`schedule-panel card border-0 shadow-sm${
            activeStep === "publish" ? " schedule-panel--publish" : ""
          }`}
        >
          {activeStep === "setup" && (
            <div className="schedule-panel__section">
              <div className="schedule-group-type" role="radiogroup" aria-label="Group type">
                {[GROUP_TYPE.SINGLE, GROUP_TYPE.MULTIPLE].map((type) => (
                  <label
                    key={type}
                    className={`schedule-group-type__option${
                      wizard.groupType === type ? " is-selected" : ""
                    }${scheduleCreated || scheduleMutationsLocked ? " is-locked" : ""}`}
                  >
                    <input
                      type="radio"
                      name="groupType"
                      value={type}
                      checked={wizard.groupType === type}
                      disabled={scheduleCreated || scheduleMutationsLocked}
                      onChange={() => updateWizard({ groupType: type })}
                    />
                    <span className="schedule-group-type__title">{type}</span>
                    <span className="schedule-group-type__desc">
                      {type === GROUP_TYPE.SINGLE
                        ? "One cohort, fastest setup."
                        : "Multiple cohorts with shared or per-group visits."}
                    </span>
                  </label>
                ))}
              </div>

              <div className="schedule-toggles">
                {SETUP_TOGGLES.map((toggle) => {
                  if (
                    toggle.dependsOn &&
                    !wizard.setupFlags[toggle.dependsOn]
                  ) {
                    return null;
                  }
                  return (
                    <ToggleRow
                      key={toggle.key}
                      id={`toggle-${toggle.key}`}
                      label={toggle.label}
                      hint={toggle.hint}
                      offImpact={toggle.offImpact}
                      checked={wizard.setupFlags[toggle.key]}
                      disabled={scheduleMutationsLocked}
                      onChange={(v) => updateFlag(toggle.key, v)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {(activeStep === "groups-visits" || activeStep === "visits") && (
            <div className="schedule-panel__section schedule-panel__section--visits">
              <ScheduleVisitBuilder
                mode={visitBuilderMode}
                visits={wizard.visitDrafts}
                onVisitsChange={(next) =>
                  setWizard((prev) => ({
                    ...prev,
                    visitDrafts: visitsForVisitRail(next),
                    noOfVisits: visitsWithoutAdhoc(next).length,
                  }))
                }
                cohorts={visitCohorts}
                onCohortVisitsChange={(groupId, next) =>
                  setWizard((prev) => {
                    const cohortMeta = visitCohorts.find((c) => c.id === groupId);
                    const cleaned = visitsForVisitRail(next);
                    if (cohortMeta?.isStudyScreeningGroup) {
                      return {
                        ...prev,
                        groupVisitDrafts: {
                          ...prev.groupVisitDrafts,
                          [groupId]: cleaned,
                        },
                      };
                    }
                    if (cohortMeta?.isSharedSchedule) {
                      const treatmentIds = treatmentGroupNames(prev.groupNames)
                        .map(
                          (group) =>
                            pick(group, "GroupNameId", "groupNameId") ||
                            pick(group, "GroupNameIdRef", "groupNameIdRef"),
                        )
                        .filter(Boolean);
                      const groupVisitDrafts = { ...prev.groupVisitDrafts };
                      treatmentIds.forEach((id) => {
                        groupVisitDrafts[id] = cleaned;
                      });
                      return {
                        ...prev,
                        visitDrafts: cleaned,
                        groupVisitDrafts,
                        noOfVisits: visitsWithoutAdhoc(cleaned).length,
                      };
                    }
                    return {
                      ...prev,
                      groupVisitDrafts: {
                        ...prev.groupVisitDrafts,
                        [groupId]: cleaned,
                      },
                      noOfVisits: visitsWithoutAdhoc(cleaned).length,
                    };
                  })
                }
                showGroupTabs
                studyScreening={wizard.setupFlags.studyScreening}
                setupFlags={wizard.setupFlags}
                studyScheduleId={cohort?.studyScheduleId || cohortSaveStudyId}
                trialStudyId={trialStudyId}
                organizationId={baseIds.organizationId}
                createdBy={baseIds.createdBy}
                screeningRandomisationTogether={
                  wizard.setupFlags.screeningRandomisationTogether
                }
                onNotify={({ message, type }) =>
                  showAlert({ title: "Visit setup", message, type })
                }
                onAuditEvent={recordAudit}
                onAuditLogRefresh={loadAuditLog}
                onReadinessRefresh={bumpReadiness}
                onVisitPlannerSnapshotPatch={handleVisitPlannerSnapshotPatch}
                onPromptAuditedVisitAction={promptAuditedVisitAction}
                onAddVisit={handleAddVisit}
                onSaveVisitDetails={handleSaveVisitDetails}
                onRequestRemoveVisit={handleRequestRemoveVisit}
                onReorderVisit={handleReorderVisit}
                savingVisitKey={savingVisitKey}
                structureBusy={visitStructureBusy}
                structureLocked={scheduleMutationsLocked}
                addVisitLocked={isStudyArchived}
                scheduleReadOnly={isStudyArchived}
                isScheduleLive={isScheduleLive}
                isStudyArchived={isStudyArchived}
                allGroupsSameSchedule={wizard.allGroupsSameSchedule}
                groupManageProps={groupManageProps}
              />
            </div>
          )}

          {activeStep === "publish" && (
            <div className="schedule-panel__section schedule-panel__section--publish">
              <div className="schedule-publish-callout">
                <i className="fas fa-rocket" aria-hidden />
                <div>
                  <strong>
                    {isStudyArchived
                      ? "Archived study"
                      : isScheduleLive
                        ? "Live schedule"
                        : "Go live"}
                  </strong>
                  <p>
                    {previewVisits.length} visit(s) · {wizard.groupType.toLowerCase()}
                    {isStudyArchived
                      ? " · This study is archived. Schedule data is view-only."
                      : isScheduleLive
                        ? " · This schedule is live. Review readiness and use AI Summary to see how appointments will flow."
                        : " · Review what is complete and what remains before publishing."}
                  </p>
                </div>
                <div className="schedule-publish-callout__actions">
                  <ScheduleActivityAiSummaryButton
                    auditLog={auditLog}
                    wizard={wizard}
                    cohort={cohort}
                    scheduleCreated={scheduleCreated}
                    visitBuilderMode={visitBuilderMode}
                    visitCohorts={visitCohorts}
                    studyScheduleId={cohort?.studyScheduleId || cohortSaveStudyId}
                    organizationId={baseIds.organizationId}
                    createdBy={baseIds.createdBy}
                    isScheduleLive={isScheduleLive}
                    className="btn btn-sm schedule-publish-callout__ai-summary"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={goNext}
                  >
                    View activity
                  </button>
                  {!isScheduleLive && !isStudyArchived ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      disabled={submitting || publishSummaryLoading}
                      onClick={handlePublish}
                    >
                      {publishSummaryLoading
                        ? "Checking…"
                        : submitting
                          ? "Publishing…"
                          : "Publish schedule"}
                    </button>
                  ) : null}
                </div>
              </div>
              <SchedulePreviewPanel
                embedded
                wizard={wizard}
                cohort={cohort}
                scheduleCreated={scheduleCreated}
                isScheduleLive={isScheduleLive}
                visitBuilderMode={visitBuilderMode}
                visitCohorts={visitCohorts}
                studyScheduleId={cohort?.studyScheduleId || cohortSaveStudyId}
                organizationId={baseIds.organizationId}
                createdBy={baseIds.createdBy}
                trialStudyId={trialStudyId}
                studyFromNav={studyFromNav}
                onGoToStep={goToStepById}
              />
            </div>
          )}

          {activeStep === "activity" && (
            <div className="schedule-panel__section schedule-panel__section--activity">
              <ScheduleActivityLogPanel
                auditLog={auditLog}
                loading={auditLoading}
                onRefresh={loadAuditLog}
                variant="embedded"
                studyLabel={studyNumber || studyTitle}
                wizard={wizard}
                cohort={cohort}
                scheduleCreated={scheduleCreated}
                isScheduleLive={isScheduleLive}
                visitBuilderMode={visitBuilderMode}
                visitCohorts={visitCohorts}
                studyScheduleId={cohort?.studyScheduleId || cohortSaveStudyId}
                organizationId={baseIds.organizationId}
                createdBy={baseIds.createdBy}
              />
            </div>
          )}

          {activeStep === "setup" && (
            <div className="schedule-panel__actions">
              {!isStudyArchived && (!scheduleCreated || setupFlagsChanged()) ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={submitting}
                  onClick={handleSetupSaveOnly}
                >
                  {submitting
                    ? "Saving…"
                    : scheduleCreated
                      ? "Update"
                      : "Save"}
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                disabled={submitting}
                onClick={handleSetupContinue}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      )}
        </div>
      </div>
      <ConfirmModal
        isOpen={saveConfirm.open}
        onClose={closeSaveConfirm}
        onConfirm={() => saveConfirm.onProceed?.()}
        title={saveConfirm.title}
        message={saveConfirm.message}
        confirmText={saveConfirm.confirmText || "Continue"}
        cancelText="Cancel"
        confirmButtonClass={saveConfirm.confirmButtonClass || "btn-primary"}
        icon="fas fa-exclamation-triangle text-warning"
      />
    </div>
  );
}
